import { DefaultAction } from "./action-button-schema"
import { resolveTemplateString, resolveTemplateStringAndLogErrors } from "../../helpers/resolve-template-string"
import { ComponentNamespace, getPubsub, publishWith, publish } from "../../redux/pubsub"
import { runStartRule } from "../../helpers/run-start-rule"
import { timestamp } from "../../helpers/PublishUtils"
import { toast } from "react-toastify"
import axios from "axios"
import { exportAsFile } from "../../utils/export-as-file"
import { SparqlManager } from "../../helpers/SparqlManager"
import { assertNever } from "../../utils/assert-never"
import { WistorAppCtx } from "../../hooks/use-app-ctx"

export async function dispatchDefaultActions(
  action: DefaultAction,
  sparqlEndpoint: string,
  namespace: ComponentNamespace,
  sourceId: string,
  appCtx: WistorAppCtx,
  expressionVars?: Record<string, string>
): Promise<void> {
  const vars = { ...getPubsub(namespace) as Record<string, string>, ...expressionVars }

  let success: boolean
  switch (action.type) {

    case "query-to-file":
      try {
        switch (action.mediaType) {
          case 'text/turtle':
            const sparqlManager = new SparqlManager(sparqlEndpoint, sourceId, namespace)
            const constructQuery = resolveTemplateString(action.constructQuery, vars)
            const ttl = await sparqlManager.fetchTurtle(constructQuery)
            exportAsFile(ttl, action.desiredFilename)
            success = true
            break
          default: assertNever(action.mediaType)
        }
      } catch (e) {
        console.error(e)
        success = false
      }
      break

    case "fetch-file":
      try {
        const res = await axios.get(action.url)
        exportAsFile(res.data, action.desiredFilename)
        success = true
      } catch (e) {
        console.error(e)
        success = false
      }
      break

    case "cancel-job":
      try {
        if (appCtx.type !== "wistor-backend") throw new Error("Need Wistor backend to cancel a job")

        const resolvedId = resolveTemplateString(action.jobId, vars)
        const urlParams = new URLSearchParams()
        if (action.gracefulCancellationTimeout) {
          urlParams.set("gracefulCancellationTimeout", action.gracefulCancellationTimeout)
        }
        const url = appCtx.makeUrl(ctx =>`${ctx.origin}/servlets/cgi/api/jobs/${resolvedId}/cancel?${urlParams.toString()}`)
        await axios.post(url, null, {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json"
          }
        })
        success = true
      } catch (e) {
        console.error(e)
        success = false
      }
      break

    case "publish": 
      try {
        const resolvedValue = resolveTemplateString(action.value, vars)
        const actionId = `${sourceId} -- publish action`
        publish(action.topic, resolvedValue, namespace, actionId)
        success = true
      } catch (e) {
        success = false
        console.error(e)
      }
      break

    case "start-rule":
      try {
        await runStartRule(
          action.startRule,
          action.parameters ? JSON.stringify(action.parameters) : null,
          namespace,
          vars,
        )
        const actionId = `${sourceId} -- start rule action`
        timestamp(publishWith(namespace, actionId), action.timestampVariable, vars)
        success = true
      } catch (e) {
        success = false
        // console.error(e) //this is commented out because descendant function logs errors
      }
      break

  }

  showActionNotificationToast(action, vars, success, defaultFallbackMessage)
}

export function showActionNotificationToast<T extends string>(
  action: {
    type: T,
    notification?: DefaultAction["notification"]
  },
  vars: Record<string, string>,
  success: boolean,
  fallbacks: FallbackMessages<T>) {
  const resolveValue = (template?: string) => {
    if (!template) return null
    return resolveTemplateStringAndLogErrors(template, vars)
  }

  const notification = success
    ? resolveValue(action.notification?.onSuccess ?? fallbacks[action.type]?.onSuccess)
    : resolveValue(action.notification?.onFailure ?? fallbacks[action.type]?.onFailure)
  if (!notification) return
  toast(notification)
}

type FallbackMessages<T extends string> = Partial<Record<T, DefaultAction["notification"]>>

export const defaultFallbackMessage = {
  "fetch-file": {
    onFailure: "failed to fetch file"
  },
  "publish": {
    onFailure: "failed to published app variable"
  },
  "start-rule": {
    onFailure: "failed to run rule(-chain) completely"
  },
  "query-to-file": {
    onFailure: "failed to construct to ttl file"
  },
  "cancel-job": {
    onFailure: "failed to cancel job"
  },
} as const satisfies Record<DefaultAction['type'], Partial<Record<'onSuccess' | 'onFailure', string>>>
